import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { api } from '../../service/api';
import {
  Dimmer,
  Loader,
  Table,
  Modal,
  Button,
  ButtonGroup,
} from 'semantic-ui-react';
import ErrorBoundary from '../Common/ErrorBoundary';
import { JsonEditor as Editor } from 'jsoneditor-react';
import 'jsoneditor-react/es/editor.min.css';
import { showNotification } from '../../redux/actions/notification';
import { useDispatch } from 'react-redux';
import Filters from '../Common/Filters';

type ClientModelParametersProps = {
  clientId: string;
};

interface ModelParams {
  id: Number;
  Property_Code: string;
  ModelType: string;
  Parameters: any;
  CreatedOn: string;
  UpdatedOn: string;
}

const ClientModelParameters = (props: ClientModelParametersProps) => {
  const { clientId } = props;
  const [modelParams, setModelParams] = useState<ModelParams[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewParams, setViewParams] = useState<ModelParams | null>(null);
  const [editedParams, setEditedParams] = useState<ModelParams[] | null>();

  const [filteredModelParams, setFilteredModelParams] = useState<
    ModelParams[] | null
  >();

  const dispatch = useDispatch();
  useEffect(() => {
    setIsLoading(true);
    api.getClientModelParameters(clientId).then((res) => {
      setModelParams(res.data);
      setFilteredModelParams(res.data);
      setIsLoading(false);
    });
  }, [clientId]);

  const handleParameterChange = (data) => {
    setEditedParams(data);
  };

  const saveParameters = () => {
    if (
      editedParams &&
      JSON.stringify(viewParams.Parameters) !== JSON.stringify(editedParams)
    ) {
      const { id, Property_Code, ModelType } = viewParams;
      api
        .updateClientModelParameters({
          clientId,
          params: {
            id,
            Property_Code,
            ModelType,
            Parameters: editedParams,
          },
        })
        .then((res) => {
          setModelParams(res.data);
          setViewParams(null);
          dispatch(showNotification(`Model parameters updated`, 'info'));
        })
        .catch((err) => {
          setViewParams(null);
          dispatch(
            showNotification('Failed to updated model parameters', 'warning')
          );
        });
    }
  };

  return (
    <div>
      {isLoading ? (
        <Dimmer active>
          <Loader />
        </Dimmer>
      ) : null}
      {modelParams?.length > 0 && (
        <Filters
          filteredLists={filteredModelParams}
          originalList={modelParams}
          allowedParams={['Property_Code', 'ModelType']}
          setFilteredLists={setFilteredModelParams}
        />
      )}
      {modelParams && (
        <div>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Property Code</Table.HeaderCell>
                <Table.HeaderCell>Model Type</Table.HeaderCell>
                <Table.HeaderCell>CreatedOn</Table.HeaderCell>
                <Table.HeaderCell>UpdatedOn</Table.HeaderCell>
                <Table.HeaderCell>Parameters</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredModelParams?.map((el) => {
                return (
                  <Table.Row>
                    <Table.Cell>{el.Property_Code}</Table.Cell>
                    <Table.Cell>{el.ModelType}</Table.Cell>
                    <Table.Cell>{el.CreatedOn}</Table.Cell>
                    <Table.Cell>{el.UpdatedOn}</Table.Cell>
                    <Table.Cell>
                      <Button onClick={() => setViewParams(el)}>View</Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {viewParams && (
            <Modal open={!!viewParams} onClose={() => setViewParams(null)}>
              <Modal.Content>
                <ErrorBoundary>
                  <Editor
                    value={viewParams.Parameters}
                    mode="code"
                    onChange={handleParameterChange}
                    htmlElementProps={{
                      style: { minHeight: 400, height: 400 },
                    }}
                    allowedModes={['tree', 'code']}
                  />
                  <div>
                    <ButtonGroup size="small">
                      <Button disabled={false} primary onClick={saveParameters}>
                        Save
                      </Button>
                      <Button negative onClick={() => setViewParams(null)}>
                        Close
                      </Button>
                    </ButtonGroup>
                  </div>
                </ErrorBoundary>
              </Modal.Content>
            </Modal>
          )}
        </div>
      )}
    </div>
  );
};

ClientModelParameters.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default ClientModelParameters;
